'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { usePipelineStore } from '@/lib/pipeline/store';
import { useToast } from '@/components/Toast';
import PipelineLibraryModal from './PipelineLibraryModal';

export default function PipelineMenu() {
  const toast = useToast();
  const router = useRouter();
  const clearGraph = usePipelineStore((s) => s.clearGraph);

  const [current, setCurrent] = useState<{ id: number; name: string } | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const serialize = () => {
    const { nodes, edges } = usePipelineStore.getState();
    return JSON.stringify({ version: 1, nodes, edges });
  };

  const onNew = () => {
    clearGraph();
    setCurrent(null);
  };

  const onSave = async () => {
    const { nodes } = usePipelineStore.getState();
    if (!nodes.length) {
      toast.error('Nothing to save');
      return;
    }
    if (current) {
      await api.updatePipeline(current.id, { graph_json: serialize() });
      toast.success(`Saved "${current.name}"`);
    } else {
      setName('');
      setSaveOpen(true);
    }
  };

  const onConfirmSave = async () => {
    const n = name.trim();
    if (!n) return;
    const p = await api.createPipeline(n, serialize());
    setCurrent({ id: p.id, name: p.name });
    setSaveOpen(false);
    toast.success(`Saved "${n}"`);
  };

  // Save the current graph, then open the Batch page for this pipeline.
  const onRunBatch = async () => {
    if (!current) {
      toast.error('Save the pipeline first to run it as a batch');
      return;
    }
    try {
      await api.updatePipeline(current.id, { graph_json: serialize() });
    } catch {
      /* fall back to the last-saved version */
    }
    router.push(`/pipeline/batch?pipeline=${current.id}`);
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={onNew} className="btn btn-ghost h-9 px-3 text-sm shadow-soft">
        New
      </button>
      <button onClick={() => setLibraryOpen(true)} className="btn btn-ghost h-9 px-3 text-sm shadow-soft">
        Library
      </button>
      <button onClick={onSave} className="btn btn-ghost h-9 px-3 text-sm shadow-soft">
        Save
      </button>
      <button
        onClick={onRunBatch}
        disabled={!current}
        className="btn btn-ghost h-9 px-3 text-sm shadow-soft disabled:opacity-40 disabled:cursor-not-allowed"
        title={current ? 'Run this pipeline over a list of inputs' : 'Save the pipeline first'}
      >
        Run batch
      </button>
      {current && (
        <span className="text-xs text-[var(--text-muted)] max-w-[120px] truncate" title={current.name}>
          {current.name}
        </span>
      )}

      {libraryOpen && (
        <PipelineLibraryModal onClose={() => setLibraryOpen(false)} onPick={setCurrent} />
      )}

      {saveOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setSaveOpen(false)}
        >
          <div className="bg-[var(--bg-elevated)] rounded-xl p-5 w-80" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-3">Save pipeline</h3>
            <input
              autoFocus
              className="input w-full text-sm"
              placeholder="Pipeline name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onConfirmSave();
              }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setSaveOpen(false)} className="btn btn-ghost h-8 px-3 text-sm">
                Cancel
              </button>
              <button onClick={onConfirmSave} className="btn btn-primary h-8 px-4 text-sm">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
